import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLang } from "../LangContext";
import { LANGS, LOGO_URL } from "../i18n";
import { Menu, X } from "lucide-react";

export default function Header() {
    const { lang, setLang, t } = useLang();
    const [open, setOpen] = useState(false);
    const loc = useLocation();
    const onHome = loc.pathname === "/";

    const link = (href, label, id) => (
        <a
            key={id}
            href={onHome ? href : `/${href}`}
            data-testid={`nav-${id}`}
            onClick={() => setOpen(false)}
            className="text-sm font-medium uppercase tracking-wider text-zinc-300 hover:text-volt transition-colors"
        >
            {label}
        </a>
    );

    return (
        <header
            data-testid="site-header"
            className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-ink/70 border-b border-white/5"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
                <Link to="/" data-testid="logo-home" className="flex items-center gap-3 group">
                    <img
                        src={LOGO_URL}
                        alt="Urban Rookies"
                        className="logo-invert h-12 md:h-14 w-auto object-contain"
                    />
                </Link>

                <nav className="hidden lg:flex items-center gap-5 xl:gap-7">
                    {link("#mission", t.nav.mission, "mission")}
                    {link("#team", t.nav.team, "team")}
                    {link("#inspiration", t.nav.inspiration, "inspiration")}
                    {link("#partners", t.nav.partners, "partners")}
                    {link("#program", t.nav.program, "program")}
                    {link("#events", t.nav.events, "events")}
                    {link("#news", t.nav.news, "news")}
                    {link("#donate", t.nav.help, "help")}
                </nav>

                <div className="flex items-center gap-3">
                    <div className="hidden md:flex items-center gap-1 border border-white/10 px-1 py-1 text-xs">
                        {LANGS.map((l) => (
                            <button
                                key={l}
                                data-testid={`lang-switch-${l.toLowerCase()}`}
                                onClick={() => setLang(l)}
                                className={`px-2 py-1 font-mono transition-colors ${
                                    lang === l ? "bg-volt text-black" : "text-zinc-400 hover:text-white"
                                }`}
                            >
                                {l}
                            </button>
                        ))}
                    </div>
                    <a
                        href={onHome ? "#donate" : "/#donate"}
                        data-testid="header-donate-btn"
                        className="btn-volt px-5 py-2.5 text-xs hidden xl:inline-block"
                    >
                        {t.nav.donate}
                    </a>
                    <button
                        className="lg:hidden text-white p-2"
                        onClick={() => setOpen(!open)}
                        data-testid="mobile-menu-toggle"
                        aria-label="Toggle menu"
                    >
                        {open ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>
            </div>

            {open && (
                <div className="lg:hidden border-t border-white/10 bg-ink/95 px-6 py-6 flex flex-col gap-4">
                    {link("#mission", t.nav.mission, "mission-m")}
                    {link("#team", t.nav.team, "team-m")}
                    {link("#inspiration", t.nav.inspiration, "inspiration-m")}
                    {link("#partners", t.nav.partners, "partners-m")}
                    {link("#program", t.nav.program, "program-m")}
                    {link("#events", t.nav.events, "events-m")}
                    {link("#news", t.nav.news, "news-m")}
                    {link("#donate", t.nav.help, "help-m")}
                    <div className="flex items-center gap-1 border border-white/10 px-1 py-1 text-xs w-fit">
                        {LANGS.map((l) => (
                            <button
                                key={l}
                                onClick={() => setLang(l)}
                                data-testid={`lang-switch-m-${l.toLowerCase()}`}
                                className={`px-2 py-1 font-mono transition-colors ${
                                    lang === l ? "bg-volt text-black" : "text-zinc-400"
                                }`}
                            >
                                {l}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </header>
    );
}
